import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { PrequalCard } from "@/components/PrequalCard";
import { StatusBadge } from "@/components/StatusBadge";
import { Pipeline } from "@/components/Pipeline";
import { AuthImage, fileDownloadUrl } from "@/components/AuthImage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { currency } from "@/lib/status";
import { monthlyPayment, APR_BAND } from "@/lib/finance";
import { promoForVehicle, promoBadge } from "@/lib/promotions";
import { Slider } from "@/components/ui/slider";
import { ArrowLeft, Landmark, Car, FolderCheck, Truck, Upload, CheckCircle2, FileText, Clock, ShieldCheck, Receipt, Calculator, XCircle, CircleAlert, ChevronRight, Building2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";


const PT = "America/Los_Angeles";
const fmtTime = (iso) => new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: PT });

function DeliveryCountdown({ r }) {
  const d = r.delivery;
  const [now, setNow] = useState(Date.now());
  useEffect(() => { if (!d?.start_at) return; const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, [d?.start_at]);
  if (!d?.confirmed || !d.confirmed_date) return null;
  const kind = d.method === "delivery" ? "Delivery" : "Pickup";
  if (d.start_at) {
    const ms = new Date(d.start_at) - now;
    if (ms < -3600000 * 6) return null;
    const h = Math.floor(ms / 3600000), m = Math.floor((ms % 3600000) / 60000), s = Math.floor((ms % 60000) / 1000);
    const days = Math.floor(h / 24);
    const rel = ms <= 0 ? `${kind} is happening now` : days >= 1 ? `${kind} in ${days}d ${h % 24}h` : h >= 1 ? `${kind} starts in ${h}h ${m}m` : `${kind} starts in ${m}m ${s}s`;
    const time = `${fmtTime(d.start_at)}${d.end_at ? ` – ${fmtTime(d.end_at)}` : ""} PT`;
    return (
      <span data-testid="delivery-countdown" className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-rose-50 px-3 py-1.5 font-mono text-xs font-bold text-rose-600">
        <Truck className="h-3.5 w-3.5" /> {rel} · {time}
      </span>
    );
  }
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const target = new Date(d.confirmed_date + "T00:00:00");
  const days = Math.round((target - today) / 86400000);
  if (isNaN(days) || days < 0) return null;
  const label = days === 0 ? "Delivery today" : days === 1 ? "Delivery tomorrow" : `Delivery in ${days} days`;
  const text = label.replace("Delivery", kind);
  return (
    <span data-testid="delivery-countdown" className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-bold text-rose-600">
      <Truck className="h-3.5 w-3.5" /> {text}{d.time_window ? ` · ${d.time_window}` : ""}
    </span>
  );
}

function DocsProgressRing({ r }) {
  const uploadItems = (r.checklist || []).filter((c) => c.type !== "acknowledge");
  const total = uploadItems.length;
  const docs = (r.documents || []).filter((d) => d.doc_type !== "tradein_photo");
  const verified = uploadItems.filter((item) => docs.some((d) => d.doc_type === item.key && d.status === "verified")).length;
  const pct = total > 0 ? verified / total : 0;
  const R = 26, C = 2 * Math.PI * R;
  const done = total > 0 && verified === total;
  return (
    <div className="mt-6 flex items-center gap-4 border-t border-slate-100 dark:border-slate-800 pt-5" data-testid="docs-progress-ring">
      <div className="relative h-16 w-16 shrink-0">
        <svg className="h-16 w-16 -rotate-90" viewBox="0 0 64 64">
          <circle cx="32" cy="32" r={R} fill="none" stroke="#e2e8f0" strokeWidth="6" />
          <circle cx="32" cy="32" r={R} fill="none" stroke={done ? "#059669" : "#1D4ED8"} strokeWidth="6" strokeLinecap="round" strokeDasharray={C} strokeDashoffset={C * (1 - pct)} style={{ transition: "stroke-dashoffset 0.6s ease" }} />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-sm font-black text-slate-900 dark:text-slate-100" data-testid="docs-progress-count">{verified}/{total}</span>
      </div>
      <div>
        <p className="text-sm font-bold text-slate-900 dark:text-slate-100">Documents verified</p>
        <p className="text-xs text-slate-500 dark:text-slate-400">{done ? "All documents verified — you're ready to close." : `${total - verified} still to be verified by your broker.`}</p>
      </div>
    </div>
  );
}

function ChecklistMark({ complete, pending = false }) {
  if (complete) {
    return <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />;
  }
  if (pending) {
    return <Clock className="h-4 w-4 shrink-0 text-amber-600" />;
  }
  return <CircleAlert className="h-4 w-4 shrink-0 text-rose-600" />;
}

function FinancingProgressChecklist({ r, onTabChange }) {
  const financing = r.financing || {};
  const prequal = r.prequalification || {};
  const docs = (r.documents || []).filter((doc) => doc.doc_type !== "tradein_photo");
  const verifiedIncomeDocument = docs.some(
    (doc) => doc.doc_type === "proof_of_income" && doc.status === "verified",
  );
  const identityComplete = Boolean(financing.full_name && financing.date_of_birth && financing.ssn_last4);
  const incomeOnFile = Boolean(
    financing.annual_income || prequal.monthly_income || verifiedIncomeDocument,
  );
  const employmentProfileOnFile = Boolean(
    (financing.employment_status || prequal.employment_status) &&
    (financing.employer_name || prequal.employer_name),
  );
  const employmentDetailsComplete = Boolean(
    financing.employer_address && financing.employment_start_date,
  );
  const employmentComplete = incomeOnFile && employmentProfileOnFile && employmentDetailsComplete;
  const housingComplete = Boolean(
    financing.residence_status && financing.monthly_housing !== null && financing.monthly_housing !== undefined &&
    financing.monthly_debts !== null && financing.monthly_debts !== undefined,
  );
  const loanComplete = Boolean(
    financing.down_payment !== null && financing.down_payment !== undefined &&
    financing.requested_term_months && financing.credit_tier && financing.consent_share,
  );
  const financingComplete = identityComplete && employmentComplete && housingComplete && loanComplete;
  const submitted = Boolean(financing.submitted_at);
  const financePending = submitted && !["approved", "declined"].includes(financing.status);
  const policy = r.financing_document_requirements || {};
  const uploadItems = [
    { key: "drivers_license_front", label: "Government photo ID — front" },
    { key: "drivers_license_back", label: "Government photo ID — back" },
    { key: "proof_of_income", label: "Most recent pay stub / proof of income" },
    ...(policy.require_residence ? [{ key: "residence", label: "Proof of residence" }] : []),
  ];
  const missingDocs = uploadItems.filter((item) => !docs.some((doc) => doc.doc_type === item.key));
  const verifyingDocs = uploadItems.filter((item) => docs.some(
    (doc) => doc.doc_type === item.key && doc.status !== "verified",
  ));
  const docsComplete = uploadItems.length > 0 && missingDocs.length === 0 && verifyingDocs.length === 0;
  const remainingFields = [
    !identityComplete && "Identity details",
    !incomeOnFile && "Income",
    !employmentProfileOnFile && "Employment status and employer",
    !employmentDetailsComplete && "Employer address and start date",
    !housingComplete && "Housing and monthly debts",
    !loanComplete && "Loan preferences and authorization",
  ].filter(Boolean);
  const incompleteDocuments = [
    ...missingDocs.map((item) => item.label),
    ...verifyingDocs.map((item) => `${item.label} (being verified)`),
  ];
  const nextTab = !financingComplete || !submitted ? "financing" : !docsComplete ? "documents" : "financing";
  const nextLabel = !financingComplete || !submitted
    ? "Complete financing"
    : !docsComplete
      ? "Review documents"
      : "View financing";

  return (
    <section
      className="mt-6 rounded-xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900"
      data-testid="buyer-financing-progress-checklist"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-rose-600">Your next steps</p>
          <h3 className="mt-1 font-display text-lg font-bold text-slate-900 dark:text-slate-100">
            Financing checklist
          </h3>
        </div>
        <span
          className="rounded-full bg-slate-100 px-2 py-1 text-xs font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300"
          data-testid="buyer-financing-progress-count"
        >
          {[financingComplete && submitted, docsComplete].filter(Boolean).length}/2 complete
        </span>
      </div>

      <div className="mt-4 space-y-3">
        <div className="flex gap-3" data-testid="buyer-financing-application-step">
          <ChecklistMark complete={financingComplete && submitted} pending={financePending} />
          <div className="min-w-0">
            <p className="text-sm font-bold text-slate-900 dark:text-slate-100">Financing application</p>
            <p className="mt-0.5 text-xs leading-5 text-slate-500 dark:text-slate-400">
              {financingComplete && submitted
                ? financePending
                  ? "Submitted — your broker is reviewing it."
                  : financing.status === "approved"
                    ? "Approved — see your financing decision."
                    : "Submitted — see your financing update."
                : remainingFields.length
                  ? `Still needed: ${remainingFields.join(", ")}.`
                  : "Complete and submit your application for broker review."}
            </p>
          </div>
        </div>
        <div className="flex gap-3" data-testid="buyer-financing-documents-step">
          <ChecklistMark complete={docsComplete} pending={verifyingDocs.length > 0} />
          <div className="min-w-0">
            <p className="text-sm font-bold text-slate-900 dark:text-slate-100">Required documents</p>
            <p className="mt-0.5 text-xs leading-5 text-slate-500 dark:text-slate-400">
              {docsComplete
                ? "All requested documents are verified."
                : incompleteDocuments.length
                  ? `Still needed: ${incompleteDocuments.join(", ")}.`
                  : "Your broker will request documents when they are needed."}
            </p>
          </div>
        </div>
      </div>

      <Button
        type="button"
        variant="outline"
        onClick={() => onTabChange(nextTab)}
        className="mt-5 h-10 w-full justify-between rounded-md text-sm font-bold"
        data-testid="buyer-financing-progress-action"
      >
        {nextLabel}
        <ChevronRight className="h-4 w-4" />
      </Button>
    </section>
  );
}

export default function RequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "overview";
  const [r, setR] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/requests/${id}`); setR(data); }
    catch { navigate("/dashboard"); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (!r) return <div className="min-h-screen bg-slate-50 dark:bg-slate-950"><Navbar /></div>;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <Navbar />
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <button onClick={() => navigate("/dashboard")} className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-slate-500 dark:text-slate-400 hover:text-slate-900" data-testid="back-btn"><ArrowLeft className="h-4 w-4" /> My deals</button>

        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">{r.source === "dealer_link" ? "Dealer link request" : "Inventory request"}</p>
            <h1 className="font-display text-3xl font-black tracking-tighter text-slate-950 dark:text-slate-50">{r.year} {r.make} {r.model}</h1>
            <p className="mt-1 text-slate-600 dark:text-slate-300">{r.trim} {r.color && `· ${r.color}`}</p>
          </div>
          <div className="flex items-center gap-3">
            <StatusBadge status={r.status} size="lg" />
            <DeliveryCountdown r={r} />
          </div>
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
              <h3 className="font-display text-lg font-bold text-slate-900 dark:text-slate-100">Progress</h3>
              <div className="mt-5"><Pipeline status={r.status} /></div>
              <FinancingProgressChecklist
                r={r}
                onTabChange={(nextTab) => setSearchParams({ tab: nextTab })}
              />
              <DocsProgressRing r={r} />
            </div>
          </div>

          <div className="lg:col-span-8">
            <Tabs value={tab} onValueChange={(v) => setSearchParams(v === "overview" ? {} : { tab: v })}>
              <TabsList className="grid w-full grid-cols-5 rounded-lg bg-slate-100 dark:bg-slate-800 p-1">
                <TabsTrigger value="overview" data-testid="tab-overview" className="rounded-md text-xs font-semibold">Overview</TabsTrigger>
                <TabsTrigger value="prequal" data-testid="tab-prequal" className="rounded-md text-xs font-semibold">Prequal</TabsTrigger>
                <TabsTrigger value="financing" data-testid="tab-financing" className="rounded-md text-xs font-semibold">Financing</TabsTrigger>
                <TabsTrigger value="tradein" data-testid="tab-tradein" className="rounded-md text-xs font-semibold">Trade-in</TabsTrigger>
                <TabsTrigger value="documents" data-testid="tab-documents" className="rounded-md text-xs font-semibold">Documents</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="mt-4"><Overview r={r} onDelivery={load} /></TabsContent>
              <TabsContent value="prequal" className="mt-4"><PrequalDetails r={r} /></TabsContent>
              <TabsContent value="financing" className="mt-4"><Financing r={r} onDone={load} /></TabsContent>
              <TabsContent value="tradein" className="mt-4"><TradeIn r={r} onDone={load} /></TabsContent>
              <TabsContent value="documents" className="mt-4"><Documents r={r} onDone={load} /></TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
}

function Card({ children, title, icon: Icon }) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
      {title && <h3 className="mb-4 flex items-center gap-2 font-display text-lg font-bold text-slate-900 dark:text-slate-100">{Icon && <Icon className="h-5 w-5 text-rose-600" />}{title}</h3>}
      {children}
    </div>
  );
}

function Row({ k, v }) {
  return <div className="flex justify-between"><span className="text-slate-600 dark:text-slate-300">{k}</span><span className="font-semibold text-slate-900 dark:text-slate-100">{v}</span></div>;
}

function Overview({ r, onDelivery }) {
  const rows = [
    ["VIN", r.vin || "Confirmed at verification"], ["Stock #", r.stock_number || "—"],
    ["MSRP", r.msrp ? currency(r.msrp) : "—"], ["Dealer", r.dealer_location || "—"],
    ["Delivery ZIP", r.delivery_zip || "—"],
  ];
  return (
    <div className="space-y-4">
      {!r.quote && (r.status === "prequal" || r.status === "requested") && <PrequalCard />}
      {r.quote && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-6">
          <h3 className="font-display text-lg font-bold text-rose-700">Your confirmed quote</h3>
          <div className="mt-3 space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-slate-600 dark:text-slate-300">Vehicle price</span><span className="font-semibold text-slate-900 dark:text-slate-100">{currency(r.quote.vehicle_price)}</span></div>
            <div className="flex justify-between"><span className="text-slate-600 dark:text-slate-300">Fees</span><span className="font-semibold text-slate-900 dark:text-slate-100">{currency(r.quote.fees)}</span></div>
            <div className="flex justify-between"><span className="text-slate-600 dark:text-slate-300">Doc fee</span><span className="font-semibold text-slate-900 dark:text-slate-100">{currency(r.quote.doc_fee)}</span></div>
            <div className="mt-2 flex justify-between border-t border-rose-200 pt-2"><span className="font-bold text-slate-900 dark:text-slate-100">Total</span><span className="font-display text-xl font-black text-rose-600">{currency(r.quote.total)}</span></div>
          </div>
          {r.quote.notes && <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">{r.quote.notes}</p>}
        </div>
      )}
      <Card title="Vehicle details" icon={Car}>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
          {rows.map(([k, v]) => (<div key={k}><dt className="text-xs font-bold uppercase tracking-wide text-slate-400 dark:text-slate-500">{k}</dt><dd className="mt-0.5 text-sm font-semibold text-slate-800 dark:text-slate-200">{v}</dd></div>))}
        </dl>
        {r.dealer_url && <a href={r.dealer_url} target="_blank" rel="noopener noreferrer" className="mt-4 inline-block text-sm font-semibold text-rose-600 hover:underline">View original dealer listing →</a>}
      </Card>
      {r.verification?.verified && r.dealer_contact && <DealerDetails contact={r.dealer_contact} />}
      {(r.status === "ready" || r.status === "ready_for_delivery" || r.delivery) && <DeliveryForm r={r} onDone={onDelivery} />}
      <Card title="Activity" icon={Clock}>
        <div className="space-y-3">
          {(r.audit_trail || []).slice().reverse().map((a, i) => (
            <div key={i} className="flex gap-3 text-sm">
              <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-rose-500" />
              <div><p className="font-semibold text-slate-800 dark:text-slate-200">{a.action.replace(/_/g, " ")}</p><p className="text-slate-500 dark:text-slate-400">{a.detail} · {new Date(a.at).toLocaleString()}</p></div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function DealerDetails({ contact }) {
  const fields = [["Dealer", contact.dealership_name], ["Address", contact.dealership_address], ["Contact person", contact.contact_person || contact.name], ["Phone", contact.phone], ["Email", contact.email]];
  return <Card title="Dealer details" icon={Building2}><p className="mb-4 text-sm text-slate-600 dark:text-slate-300" data-testid="buyer-dealer-details-note">Your broker verified this dealer for your vehicle request.</p><dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2" data-testid="buyer-dealer-details">{fields.map(([label, value]) => <div key={label} className="min-w-0 border-b border-slate-100 pb-2 dark:border-slate-800"><dt className="text-[10px] font-bold uppercase tracking-wide text-slate-400 dark:text-slate-500">{label}</dt><dd className="mt-0.5 break-words text-sm font-semibold text-slate-800 dark:text-slate-200">{value || "—"}</dd></div>)}</dl></Card>;
}

function PrequalDetails({ r }) {
  const p = r.prequalification;
  if (!p) {
    return <Card title="Prequalification" icon={ShieldCheck}><p className="text-sm text-slate-500 dark:text-slate-400" data-testid="buyer-prequal-empty">No prequalification form is available for this deal.</p></Card>;
  }
  const fields = [
    ["Status", String(p.status || "pending").replaceAll("_", " ")],
    ["Budget", p.budget_amount ? `${currency(p.budget_amount)}${p.budget_type === "payment" ? "/mo" : ""}` : "—"],
    ["Down payment", p.down_payment != null ? currency(p.down_payment) : "—"],
    ["Credit tier", p.credit_tier || "—"],
    ["Trade-in", p.trade_in ? "Yes" : "No"],
    ["Purchase timeline", p.timeline || "—"],
    ["Employment", p.employment_status || "—"],
    ["Employer / business", p.employer_name || "—"],
    ["Job title", p.job_title || "—"],
    ["Time on job", p.months_on_job ? `${p.months_on_job} months` : "—"],
    ["Monthly income", p.monthly_income ? currency(p.monthly_income) : "—"],
    ["Pay stub", p.paystub ? p.paystub.original_filename : "Not uploaded"],
  ];
  return <Card title="Your prequalification" icon={ShieldCheck}><p className="mb-4 text-sm text-slate-500 dark:text-slate-400" data-testid="buyer-prequal-finance-note">Compatible details are carried into Financing. You will still confirm sensitive and financing-specific information there.</p><dl className="grid grid-cols-2 gap-x-6 gap-y-3" data-testid="buyer-prequal-details">{fields.map(([label, value]) => <div key={label}><dt className="text-xs font-bold uppercase tracking-wide text-slate-400 dark:text-slate-500">{label}</dt><dd className="mt-0.5 text-sm font-semibold text-slate-800 dark:text-slate-200">{value}</dd></div>)}</dl></Card>;
}

function Financing({ r, onDone }) {
  const fin = r.financing;
  const prequal = r.prequalification || {};
  const requirementConfig = r.financing_document_requirements || {};
  const requiredDocuments = [
    ["drivers_license_front", "Government photo ID — front"],
    ["drivers_license_back", "Government photo ID — back"],
    ["proof_of_income", "Most recent pay stub / proof of income"],
    ...(requirementConfig.require_residence ? [["residence", "Proof of residence"]] : []),
  ];
  const submittedDocuments = new Set(
    (r.documents || []).filter((doc) => doc.status !== "rejected").map((doc) => doc.doc_type),
  );
  const missingDocuments = requiredDocuments.filter(([key]) => !submittedDocuments.has(key));
  const bd = r.financing_breakdown;
  const [finPromos, setFinPromos] = useState([]);
  useEffect(() => { api.get("/promotions/active").then(({ data }) => setFinPromos(data || [])).catch(() => {}); }, []);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [revealed, setRevealed] = useState(null);
  const [form, setForm] = useState({ full_name: fin?.full_name || user?.legal_name || r.buyer_name || "", annual_income: fin?.annual_income ?? (prequal.monthly_income ? String(Number(prequal.monthly_income) * 12) : ""), employment_status: fin?.employment_status || prequal.employment_status || "employed", employer_name: fin?.employer_name || prequal.employer_name || "", employer_address: "", employment_start_date: "", down_payment: fin?.down_payment ?? prequal.down_payment ?? (bd ? Math.round(bd.out_the_door * 0.1) : ""), requested_term_months: String(fin?.requested_term_months || 60), ssn: "", date_of_birth: "", credit_tier: fin?.credit_tier || prequal.credit_tier || "good", monthly_debts: "", consent_share: false, residence_status: "own", monthly_housing: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const reveal = async () => {
    try { const { data } = await api.post(`/requests/${r.id}/financing/reveal-ssn`); setRevealed(data.ssn); }
    catch { toast.error("Could not reveal"); }
  };
  const submit = async () => {
    const ssnDigits = (form.ssn || "").replace(/\D/g, "");
    if (!form.full_name || !form.annual_income || !form.down_payment) { toast.error("Please complete required fields"); return; }
    if (ssnDigits.length !== 9) { toast.error("Enter your full 9-digit SSN"); return; }
    if (!form.date_of_birth) { toast.error("Please enter your date of birth"); return; }
    if (!form.consent_share) { toast.error("Please authorize sharing your application to submit"); return; }
    try {
      await api.post(`/requests/${r.id}/financing`, {
        full_name: form.full_name, annual_income: parseFloat(form.annual_income), employment_status: form.employment_status,
        employer_name: form.employer_name, employer_address: form.employer_address, employment_start_date: form.employment_start_date,
        down_payment: parseFloat(form.down_payment), requested_term_months: parseInt(form.requested_term_months),
        ssn: ssnDigits, date_of_birth: form.date_of_birth, credit_tier: form.credit_tier,
        monthly_debts: form.monthly_debts ? parseFloat(form.monthly_debts) : null, consent_share: form.consent_share,
        residence_status: form.residence_status,
        monthly_housing: form.monthly_housing ? parseFloat(form.monthly_housing) : null,
      });
      toast.success("Financing application submitted for review");
      onDone();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "object" ? detail.message : detail || "Could not submit application");
    }
  };

  const downNum = Number(form.down_payment || 0);
  const termNum = Number(form.requested_term_months || 60);
  const tradeEquity = bd && bd.trade_applied ? Number(bd.trade_equity || 0) : 0;
  const financed = bd ? Math.max(bd.out_the_door - downNum - tradeEquity, 0) : null;
  const estLow = financed != null ? monthlyPayment(financed, APR_BAND.low, termNum) : 0;
  const estHigh = financed != null ? monthlyPayment(financed, APR_BAND.high, termNum) : 0;
  const finPromo = promoForVehicle(finPromos, r);
  const promoApr = finPromo && finPromo.apr != null ? finPromo.apr : null;
  const promoPrincipal = financed != null ? financed : (r.msrp ? Math.max(r.msrp - downNum - tradeEquity, 0) : null);
  const promoEst = promoApr != null && promoPrincipal != null ? monthlyPayment(promoPrincipal, promoApr, termNum) : null;

  const breakdown = bd ? (
    <Card title="Estimated California out-the-door" icon={Receipt}>
      <div className="space-y-1.5 text-sm" data-testid="fin-breakdown">
        <Row k="Vehicle price" v={currency(bd.vehicle_price)} />
        {bd.fees > 0 && <Row k="Broker fees" v={currency(bd.fees)} />}
        <Row k="Doc fee" v={currency(bd.doc_fee)} />
        <Row k={`CA sales tax (${bd.tax_rate}%)`} v={currency(bd.sales_tax)} />
        <Row k="DMV & registration (est.)" v={currency(bd.dmv_fees)} />
        <div className="flex justify-between border-t border-slate-200 dark:border-slate-800 pt-2 font-semibold text-slate-900 dark:text-slate-100"><span>Out-the-door total</span><span>{currency(bd.out_the_door)}</span></div>
        <Row k="Down payment" v={`− ${currency(downNum)}`} />
        {bd.trade_applied && <Row k="Trade-in credit (net equity)" v={`${tradeEquity >= 0 ? "−" : "+"} ${currency(Math.abs(tradeEquity))}`} />}
        <div className="flex justify-between border-t border-slate-200 dark:border-slate-800 pt-2"><span className="font-bold text-slate-900 dark:text-slate-100">Amount financed</span><span className="font-display text-lg font-black text-rose-600" data-testid="fin-amount-financed">{currency(financed)}</span></div>
      </div>
      <div className="mt-3 rounded-md border border-rose-100 bg-rose-50/70 p-2.5 text-xs text-slate-600 dark:text-slate-300" data-testid="tax-note">
        {bd.rate_source === "zip" && <>Based on your ZIP <b>{bd.zip}</b>, your California sales-tax rate is <b>{bd.tax_rate}%</b>.</>}
        {bd.rate_source === "staff" && <>Your broker set a California sales-tax rate of <b>{bd.tax_rate}%</b> for your area.</>}
        {bd.rate_source === "default" && <>Estimated at California's base rate of <b>{bd.tax_rate}%</b>. Add your ZIP in your <Link to="/profile" className="font-semibold text-rose-600 underline">profile</Link> for your exact local rate.</>}
      </div>
      <p className="mt-2 text-xs text-slate-400 dark:text-slate-500">DMV &amp; registration is an estimate; your broker confirms exact figures with your final paperwork.</p>
    </Card>
  ) : null;

  if (fin) {
    const badge = { submitted: "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-700", under_review: "bg-amber-50 text-amber-700 border-amber-200", approved: "bg-emerald-50 text-emerald-700 border-emerald-200", declined: "bg-red-50 text-red-700 border-red-200" }[fin.status];
    const totalPaid = fin.monthly_payment && fin.term_months ? fin.monthly_payment * fin.term_months : null;
    const interest = totalPaid != null && financed != null ? Math.max(totalPaid - financed, 0) : null;
    return (
      <div className="space-y-4">
        <Card title="Financing application" icon={Landmark}>
          <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-bold ${badge}`} data-testid="financing-status">{fin.status.replace(/_/g, " ").toUpperCase()}</span>
          {fin.status === "approved" && (
            <>
              <div className="mt-4 grid grid-cols-2 gap-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                <div><p className="text-xs font-bold uppercase text-slate-400 dark:text-slate-500">APR</p><p className="font-display text-xl font-bold text-emerald-700">{fin.apr}%</p></div>
                <div><p className="text-xs font-bold uppercase text-slate-400 dark:text-slate-500">Term</p><p className="font-display text-xl font-bold text-slate-900 dark:text-slate-100">{fin.term_months} mo</p></div>
                <div><p className="text-xs font-bold uppercase text-slate-400 dark:text-slate-500">Est. monthly</p><p className="font-display text-xl font-bold text-slate-900 dark:text-slate-100">{currency(fin.monthly_payment)}</p></div>
                <div><p className="text-xs font-bold uppercase text-slate-400 dark:text-slate-500">Lender</p><p className="font-semibold text-slate-800 dark:text-slate-200">{fin.lender || "—"}</p></div>
              </div>
              {totalPaid != null && (
                <div className="mt-3 flex gap-8 text-sm" data-testid="fin-approved-totals">
                  <div><p className="text-xs font-bold uppercase text-slate-400 dark:text-slate-500">Total of payments</p><p className="font-semibold text-slate-900 dark:text-slate-100">{currency(totalPaid)}</p></div>
                  {interest != null && <div><p className="text-xs font-bold uppercase text-slate-400 dark:text-slate-500">Est. total interest</p><p className="font-semibold text-slate-900 dark:text-slate-100">{currency(interest)}</p></div>}
                </div>
              )}
            </>
          )}
          {fin.decision_note && <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">{fin.decision_note}</p>}
          <div className="mt-5 border-t border-slate-200 pt-4 dark:border-slate-800" data-testid="submitted-financing-application">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Submitted application</p>
            <div className="mt-3 grid gap-x-6 gap-y-3 sm:grid-cols-2">
              <ApplicationReviewValue label="Full legal name" value={fin.full_name} />
              <ApplicationReviewValue label="Date of birth" value={fin.date_of_birth} />
              <ApplicationReviewValue label="Annual income" value={currency(fin.annual_income)} />
              <ApplicationReviewValue label="Employment" value={fin.employment_status} />
              <ApplicationReviewValue label="Employer / business" value={fin.employer_name} />
              <ApplicationReviewValue label="Employer address" value={fin.employer_address} />
              <ApplicationReviewValue label="Employment start date" value={fin.employment_start_date} />
              <ApplicationReviewValue label="Residence" value={fin.residence_status} />
              <ApplicationReviewValue label="Monthly housing" value={currency(fin.monthly_housing)} />
              <ApplicationReviewValue label="Other monthly debts" value={currency(fin.monthly_debts)} />
              <ApplicationReviewValue label="Down payment" value={currency(fin.down_payment)} />
              <ApplicationReviewValue label="Requested term" value={`${fin.requested_term_months} months`} />
              <ApplicationReviewValue label="Self-reported credit tier" value={fin.credit_tier} />
              <ApplicationReviewValue label="Authorization" value={fin.consent_share ? "Authorized" : "Not authorized"} />
              <ApplicationReviewValue label="Submitted" value={fin.submitted_at ? new Date(fin.submitted_at).toLocaleString() : "—"} />
              {fin.ssn_last4 && <div className="min-w-0 border-b border-slate-100 pb-2 dark:border-slate-800">
                <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 dark:text-slate-500">Social Security number</p>
                <p className="mt-0.5 flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-200">
                  <span data-testid="buyer-ssn-value">{revealed || `•••-••-${fin.ssn_last4}`}</span>
                  {!revealed && <button onClick={reveal} data-testid="buyer-reveal-ssn" className="text-xs font-semibold text-rose-600 hover:underline">Reveal</button>}
                  {revealed && <button onClick={() => setRevealed(null)} className="text-xs font-semibold text-slate-400 dark:text-slate-500 hover:underline">Hide</button>}
                </p>
              </div>}
            </div>
            <div className={`mt-4 border p-3 text-xs ${missingDocuments.length ? "border-amber-200 bg-amber-50 text-amber-800" : "border-emerald-200 bg-emerald-50 text-emerald-800"}`} data-testid="submitted-financing-documents">
              <p className="font-bold">Supporting documents</p>
              <p className="mt-1">{missingDocuments.length ? `Still needed: ${missingDocuments.map(([, label]) => label).join(", ")}.` : "All documents required for financing submission are on file."}</p>
            </div>
          </div>
        </Card>
        {breakdown}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {finPromo && (
        <div data-testid="fin-promo-callout" className="rounded-md border border-rose-200 bg-rose-50 p-3 dark:border-rose-900/50 dark:bg-rose-950/20">
          <p className="flex flex-wrap items-center gap-2 text-sm font-bold text-rose-700 dark:text-rose-400"><span className="rounded bg-rose-600 px-1.5 py-0.5 text-[10px] font-black uppercase text-white">{promoBadge(finPromo)}</span> {finPromo.title}</p>
          {(promoEst != null || finPromo.monthly_payment != null) && (
            <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">{promoEst != null ? <>As low as <b className="text-rose-600">{currency(promoEst)}/mo</b> at {promoApr}% APR{financed == null ? " (est. on MSRP)" : ""}.</> : <>From <b className="text-rose-600">{currency(finPromo.monthly_payment)}/mo</b>.</>}</p>
          )}
          {finPromo.description && <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{finPromo.description}</p>}
          <button onClick={() => navigate("/specials")} className="mt-2 text-xs font-bold text-rose-600 hover:underline" data-testid="fin-promo-link">See all specials →</button>
        </div>
      )}
      {breakdown}
      {bd && (
        <Card title="Estimated monthly payment" icon={Calculator}>
          <p className="font-display text-3xl font-black text-rose-600" data-testid="fin-estimate">{currency(estLow)} – {currency(estHigh)}<span className="text-base font-bold text-slate-400 dark:text-slate-500">/mo</span></p>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Indicative at {APR_BAND.low}%–{APR_BAND.high}% APR over {termNum} months. Your lender sets the final APR.</p>
          <div className="mt-5">
            <div className="flex items-baseline justify-between">
              <span className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Down payment</span>
              <span className="font-display text-lg font-bold text-slate-900 dark:text-slate-100" data-testid="slider-down-value">{currency(downNum)}</span>
            </div>
            <Slider data-testid="down-slider" value={[Math.min(downNum, Math.round(bd.out_the_door))]} min={0} max={Math.round(bd.out_the_door)} step={250} onValueChange={(v) => set("down_payment", v[0])} className="mt-3" />
            <div className="mt-1 flex justify-between text-[11px] font-medium text-slate-400 dark:text-slate-500"><span>$0</span><span>{currency(bd.out_the_door)}</span></div>
            <p className="mt-3 rounded-md bg-rose-50 px-3 py-2 text-sm text-slate-700 dark:text-slate-300">Amount financed <b className="text-rose-600">{currency(financed)}</b> over {termNum} months.</p>
          </div>
        </Card>
      )}
      {!user?.legal_name ? (
        <Card title="Complete your profile to apply" icon={Landmark}>
          <p className="mb-4 text-sm text-slate-600 dark:text-slate-300">Financing and California closing paperwork need your <b>full legal name</b> and registration address. Add them once in your profile and we'll prefill your application here.</p>
          <Button data-testid="fin-complete-profile" onClick={() => navigate("/profile")} className="w-full rounded-md bg-slate-950 py-6 font-semibold hover:bg-slate-800">Complete your profile</Button>
        </Card>
      ) : (
      <Card title="Apply for financing" icon={Landmark}>
        <p className="mb-4 text-sm text-slate-600 dark:text-slate-300">Submit your information for lender review. Our staff route your application to the selling dealer and return approval terms here. Your SSN is <b>encrypted</b> and only shared with your dealer/lender to run your credit.</p>
        {!bd && <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700">A live payment estimate appears here once your broker sends a confirmed quote.</div>}
        <div className={`mb-4 border p-3 text-sm ${missingDocuments.length ? "border-amber-200 bg-amber-50 text-amber-800" : "border-emerald-200 bg-emerald-50 text-emerald-800"}`} data-testid="financing-document-gate">
          <p className="font-bold">Documents required before submission</p>
          {missingDocuments.length ? <>
            <p className="mt-1 text-xs leading-5">Upload these in Documents before submitting your financing application:</p>
            <ul className="mt-2 list-disc pl-5 text-xs">
              {missingDocuments.map(([, label]) => <li key={label}>{label}</li>)}
            </ul>
            <Button type="button" variant="outline" onClick={() => navigate(`/requests/${r.id}?tab=documents`)} className="mt-3 h-8 border-amber-300 bg-white text-xs font-bold text-amber-900" data-testid="financing-upload-required-documents">Upload required documents</Button>
          </> : <p className="mt-1 text-xs">Required documents uploaded. You can submit after completing this application.</p>}
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2"><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Full legal name *</Label><Input data-testid="fin-name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className="mt-1.5" /></div>
          <div><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Annual income ($) *</Label><Input data-testid="fin-income" type="number" value={form.annual_income} onChange={(e) => set("annual_income", e.target.value)} className="mt-1.5" /></div>
          <div><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Down payment ($) *</Label><Input data-testid="fin-down" type="number" value={form.down_payment} onChange={(e) => set("down_payment", e.target.value)} className="mt-1.5" /></div>
          <div>
            <Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Employment</Label>
            <Select value={form.employment_status} onValueChange={(v) => set("employment_status", v)}><SelectTrigger className="mt-1.5" data-testid="fin-employment"><SelectValue /></SelectTrigger>
              <SelectContent>{["employed", "self_employed", "retired", "student", "other"].map((o) => <SelectItem key={o} value={o}>{o.replace("_", " ")}</SelectItem>)}</SelectContent></Select>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Requested term</Label>
            <Select value={form.requested_term_months} onValueChange={(v) => set("requested_term_months", v)}><SelectTrigger className="mt-1.5" data-testid="fin-term"><SelectValue /></SelectTrigger>
              <SelectContent>{["36", "48", "60", "72", "84"].map((o) => <SelectItem key={o} value={o}>{o} months</SelectItem>)}</SelectContent></Select>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Residence</Label>
            <Select value={form.residence_status} onValueChange={(v) => set("residence_status", v)}><SelectTrigger className="mt-1.5" data-testid="fin-residence"><SelectValue /></SelectTrigger>
              <SelectContent>{["own", "rent", "other"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="sm:col-span-2"><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Employer name</Label><Input data-testid="fin-employer" value={form.employer_name} onChange={(e) => set("employer_name", e.target.value)} className="mt-1.5" /></div>
          <div className="sm:col-span-2"><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Employer address</Label><Input data-testid="fin-employer-address" value={form.employer_address} onChange={(e) => set("employer_address", e.target.value)} className="mt-1.5" placeholder="Street, City, CA" /></div>
          <div className="sm:col-span-2"><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Employment start date</Label><Input data-testid="fin-start-date" type="date" value={form.employment_start_date} onChange={(e) => set("employment_start_date", e.target.value)} className="mt-1.5" /></div>
          <div><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Monthly housing ($)</Label><Input data-testid="fin-housing" type="number" value={form.monthly_housing} onChange={(e) => set("monthly_housing", e.target.value)} className="mt-1.5" /></div>
          <div><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Other monthly debts ($)</Label><Input data-testid="fin-debts" type="number" value={form.monthly_debts} onChange={(e) => set("monthly_debts", e.target.value)} className="mt-1.5" placeholder="Cards, loans, etc." /></div>
          <div>
            <Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Your credit</Label>
            <Select value={form.credit_tier} onValueChange={(v) => set("credit_tier", v)}><SelectTrigger className="mt-1.5" data-testid="fin-credit-tier"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="excellent">Excellent (720+)</SelectItem>
                <SelectItem value="good">Good (660–719)</SelectItem>
                <SelectItem value="fair">Fair (600–659)</SelectItem>
                <SelectItem value="rebuilding">Rebuilding (under 600)</SelectItem>
              </SelectContent></Select>
          </div>
          <div><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Date of birth *</Label><Input data-testid="fin-dob" type="date" value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} className="mt-1.5" /></div>
          <div><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Social Security Number *</Label><Input data-testid="fin-ssn" inputMode="numeric" maxLength={11} value={form.ssn} onChange={(e) => set("ssn", e.target.value)} className="mt-1.5" placeholder="XXX-XX-XXXX" /></div>
        </div>
        <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">Your credit tier is a quick self-estimate — no credit is pulled here. Your SSN is encrypted at rest and used only for the dealer/lender credit application.</p>
        <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 dark:border-slate-800 p-4" data-testid="fin-consent-label">
          <Checkbox checked={form.consent_share} onCheckedChange={(v) => set("consent_share", !!v)} data-testid="fin-consent" className="mt-0.5" />
          <span className="text-sm text-slate-600 dark:text-slate-300">I authorize AutoBroker to share my credit application, including my Social Security Number and date of birth, with the selling dealer and lender to process my financing.</span>
        </label>
        <Button data-testid="fin-submit" onClick={submit} disabled={missingDocuments.length > 0} className="mt-4 w-full rounded-md bg-slate-950 py-6 font-semibold hover:bg-slate-800 disabled:opacity-50">{missingDocuments.length ? "Upload required documents to continue" : "Submit application"}</Button>
      </Card>
      )}
    </div>
  );
}

function ApplicationReviewValue({ label, value }) {
  return (
    <div className="min-w-0 border-b border-slate-100 pb-2 dark:border-slate-800">
      <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 dark:text-slate-500">{label}</p>
      <p className="mt-0.5 break-words text-sm font-semibold text-slate-800 dark:text-slate-200">{value || "—"}</p>
    </div>
  );
}

function TradeIn({ r, onDone }) {
  const t = r.tradein;
  const [form, setForm] = useState({ year: "", make: "", model: "", trim: "", mileage: "", vin: "", condition: "good", payoff_amount: "", lender: "", notes: "" });
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [uploadingPayoff, setUploadingPayoff] = useState(false);
  const payoffDocs = (r.documents || []).filter((d) => d.doc_type === "tradein_payoff");
  const uploadPayoff = async (file) => {
    if (!file) return;
    setUploadingPayoff(true);
    try {
      const fd = new FormData();
      fd.append("request_id", r.id); fd.append("doc_type", "tradein_payoff"); fd.append("file", file);
      await api.post("/upload", fd);
      toast.success("Payoff statement uploaded"); onDone();
    } catch { toast.error("Upload failed"); } finally { setUploadingPayoff(false); }
  };
  const PayoffUpload = () => (
    <div className="mt-4" data-testid="ti-payoff-doc-section">
      <Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Loan payoff statement</Label>
      <p className="mb-1.5 text-xs text-slate-400 dark:text-slate-500">Upload your lender's payoff letter/statement so staff can verify your payoff amount faster. PDF, JPG or PNG.</p>
      {payoffDocs.length > 0 && (
        <div className="mb-2 space-y-1">
          {payoffDocs.map((d) => (
            <a key={d.id} href={fileDownloadUrl(d.id)} target="_blank" rel="noopener noreferrer" data-testid={`ti-payoff-doc-${d.id}`} className="flex items-center gap-2 text-sm font-medium text-rose-600 hover:underline">
              <FileText className="h-4 w-4" /> {d.original_filename}
            </a>
          ))}
        </div>
      )}
      <label className="flex cursor-pointer items-center justify-center gap-2 rounded-md border-2 border-dashed border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 py-4 text-sm font-semibold text-slate-500 dark:text-slate-400 hover:border-rose-400" data-testid="ti-payoff-doc-label">
        <Upload className="h-4 w-4" /> {uploadingPayoff ? "Uploading…" : payoffDocs.length > 0 ? "Upload another statement" : "Upload payoff statement"}
        <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" data-testid="ti-payoff-doc-input" onChange={(e) => uploadPayoff(e.target.files[0])} />
      </label>
    </div>
  );

  const upload = async (files) => {
    setUploading(true);
    try {
      const ids = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("request_id", r.id); fd.append("doc_type", "tradein_photo"); fd.append("file", file);
        const { data } = await api.post("/upload", fd);
        ids.push(data.id);
      }
      setPhotos((p) => [...p, ...ids]);
      toast.success("Photo uploaded");
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const submit = async () => {
    if (!form.year || !form.make || !form.model) { toast.error("Year, make, model required"); return; }
    try {
      await api.post(`/requests/${r.id}/tradein`, {
        year: parseInt(form.year), make: form.make, model: form.model, trim: form.trim,
        mileage: form.mileage ? parseInt(form.mileage) : null, vin: form.vin, condition: form.condition,
        payoff_amount: form.payoff_amount ? parseFloat(form.payoff_amount) : null, lender: form.lender,
        notes: form.notes, photo_ids: photos,
      });
      toast.success("Trade-in submitted for manual review");
      onDone();
    } catch { toast.error("Could not submit trade-in"); }
  };

  if (t) {
    const respond = async (accept) => {
      try { await api.patch(`/requests/${r.id}/tradein/respond`, { accept }); toast.success(accept ? "Trade-in offer accepted" : "Offer declined"); onDone(); }
      catch { toast.error("Could not respond"); }
    };
    const hasOffer = t.offer_amount != null;
    const netEquity = hasOffer ? Number(t.offer_amount || 0) - Number(t.payoff_amount || 0) : 0;
    const statusBadge = {
      pending: "border-amber-200 bg-amber-50 text-amber-700",
      offered: "border-rose-200 bg-rose-50 text-rose-600",
      accepted: "border-emerald-200 bg-emerald-50 text-emerald-700",
      declined: "border-red-200 bg-red-50 text-red-700",
    }[t.offer_status || "pending"];
    return (
      <Card title="Trade-in" icon={Car}>
        <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-bold ${statusBadge}`} data-testid="tradein-status">{(t.offer_status || "pending").replace("_", " ").toUpperCase()}</span>
        <div className="mt-4 space-y-1 text-sm text-slate-700 dark:text-slate-300">
          <p className="font-display text-lg font-bold text-slate-900 dark:text-slate-100">{t.year} {t.make} {t.model} {t.trim}</p>
          <p>{t.mileage ? `${t.mileage.toLocaleString()} mi` : ""} · Condition: {t.condition}</p>
          {t.payoff_amount != null && <p>Loan payoff: {currency(t.payoff_amount)} {t.lender && `· ${t.lender}`} {t.payoff_verified && <span className="ml-1 inline-flex items-center gap-1 text-xs font-semibold text-emerald-600"><CheckCircle2 className="h-3.5 w-3.5" /> payoff verified</span>}</p>}
          {t.notes && <p className="text-slate-600 dark:text-slate-300">{t.notes}</p>}
        </div>
        <PayoffUpload />
        {t.photo_ids?.length > 0 && (
          <div className="mt-4 grid grid-cols-3 gap-2">{t.photo_ids.map((pid) => <AuthImage key={pid} fileId={pid} alt="Trade-in" className="aspect-square w-full rounded-md object-cover" />)}</div>
        )}
        {hasOffer ? (
          <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50/60 p-4" data-testid="tradein-offer">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><p className="text-xs font-bold uppercase text-slate-400 dark:text-slate-500">Our offer</p><p className="font-display text-xl font-bold text-slate-900 dark:text-slate-100">{currency(t.offer_amount)}</p></div>
              <div><p className="text-xs font-bold uppercase text-slate-400 dark:text-slate-500">Net trade equity</p><p className={`font-display text-xl font-bold ${netEquity >= 0 ? "text-emerald-700" : "text-red-600"}`}>{netEquity >= 0 ? "" : "−"}{currency(Math.abs(netEquity))}</p></div>
            </div>
            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">Net equity {netEquity >= 0 ? "lowers" : "adds to"} your amount financed. In California, sales tax is still calculated on the full vehicle price.</p>
            {t.offer_status === "offered" && (
              <div className="mt-3 flex gap-2">
                <Button data-testid="tradein-accept" onClick={() => respond(true)} className="flex-1 rounded-md bg-emerald-600 font-semibold hover:bg-emerald-700">Accept offer</Button>
                <Button data-testid="tradein-decline" onClick={() => respond(false)} variant="outline" className="rounded-md border-red-200 font-semibold text-red-600 hover:bg-red-50">Decline</Button>
              </div>
            )}
            {t.offer_status === "accepted" && <p className="mt-3 text-sm font-semibold text-emerald-700">You accepted this offer — it's applied to your financing estimate.</p>}
            {t.offer_status === "declined" && <p className="mt-3 text-sm font-semibold text-red-600">You declined this offer.</p>}
          </div>
        ) : (
          <p className="mt-4 text-xs text-slate-400 dark:text-slate-500">Our team reviews trade-ins manually — no instant valuation. You'll get an offer after review.</p>
        )}
      </Card>
    );
  }

  return (
    <Card title="Add a trade-in" icon={Car}>
      <p className="mb-4 text-sm text-slate-600 dark:text-slate-300">Tell us about your current vehicle. We review trade-ins manually and follow up with an offer.</p>
      <div className="grid gap-4 sm:grid-cols-2">
        {[["year", "Year *", "number"], ["make", "Make *", "text"], ["model", "Model *", "text"], ["trim", "Trim", "text"], ["mileage", "Mileage", "number"], ["vin", "VIN", "text"], ["payoff_amount", "Loan payoff ($)", "number"], ["lender", "Payoff lender", "text"]].map(([k, label, type]) => (
          <div key={k}><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">{label}</Label><Input data-testid={`ti-${k}`} type={type} value={form[k]} onChange={(e) => set(k, e.target.value)} className="mt-1.5" /></div>
        ))}
        <div>
          <Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Condition</Label>
          <Select value={form.condition} onValueChange={(v) => set("condition", v)}><SelectTrigger className="mt-1.5" data-testid="ti-condition"><SelectValue /></SelectTrigger>
            <SelectContent>{["excellent", "good", "fair", "poor"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select>
        </div>
      </div>
      <div className="mt-4"><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Condition notes</Label><Textarea data-testid="ti-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="mt-1.5" placeholder="Scratches, mechanical issues, mods…" /></div>
      <div className="mt-4">
        <Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Photos</Label>
        <label className="mt-1.5 flex cursor-pointer items-center justify-center gap-2 rounded-md border-2 border-dashed border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 py-6 text-sm font-semibold text-slate-500 dark:text-slate-400 hover:border-rose-400" data-testid="ti-photo-label">
          <Upload className="h-4 w-4" /> {uploading ? "Uploading…" : "Upload photos"}
          <input type="file" accept="image/*" multiple className="hidden" data-testid="ti-photo-input" onChange={(e) => upload(Array.from(e.target.files))} />
        </label>
        {photos.length > 0 && <div className="mt-2 grid grid-cols-4 gap-2">{photos.map((pid) => <AuthImage key={pid} fileId={pid} alt="photo" className="aspect-square w-full rounded-md object-cover" />)}</div>}
      </div>
      <PayoffUpload />
      <Button data-testid="ti-submit" onClick={submit} className="mt-5 w-full rounded-md bg-slate-950 py-6 font-semibold hover:bg-slate-800">Submit trade-in</Button>
    </Card>
  );
}

function Documents({ r, onDone }) {
  const [uploading, setUploading] = useState("");
  const uploaded = (r.documents || []).filter((d) => d.doc_type !== "tradein_photo");
  const acks = r.acknowledgments || {};
  const checklist = r.checklist || [];
  const uploads = checklist.filter((c) => c.type !== "acknowledge");
  const buyerUploads = uploads.filter((c) => c.owner === "buyer");
  const brokerUploads = uploads.filter((c) => c.owner !== "buyer");
  const disclosures = checklist.filter((c) => c.type === "acknowledge");

  const upload = async (docKey, file) => {
    if (!file) return;
    setUploading(docKey);
    try {
      const fd = new FormData();
      fd.append("request_id", r.id); fd.append("doc_type", docKey); fd.append("file", file);
      await api.post("/upload", fd);
      toast.success("Document uploaded"); onDone();
    } catch { toast.error("Upload failed"); } finally { setUploading(""); }
  };
  const UploadRow = ({ item }) => {
    const docs = uploaded.filter((d) => d.doc_type === item.key);
    const done = docs.length > 0;
    const st = done ? (docs[0].status || "pending") : null;
    const verified = st === "verified";
    const rejected = st === "rejected";
    const badge = verified
      ? { c: "bg-emerald-100 text-emerald-700", t: "Verified" }
      : rejected
      ? { c: "bg-red-100 text-red-700", t: "Needs re-upload" }
      : done
      ? { c: "bg-amber-100 text-amber-700", t: "Pending review" }
      : null;
    return (
      <div data-testid={`checklist-${item.key}`} className={`flex items-center justify-between gap-4 rounded-lg border p-4 ${verified ? "border-emerald-200 bg-emerald-50/40" : rejected ? "border-red-200 bg-red-50/40" : "border-slate-200 dark:border-slate-800"}`}>
        <div className="flex items-center gap-3">
          <div className={`flex h-9 w-9 items-center justify-center rounded-full ${verified ? "bg-emerald-100 text-emerald-600" : rejected ? "bg-red-100 text-red-600" : done ? "bg-amber-100 text-amber-600" : "bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500"}`}>
            {verified ? <CheckCircle2 className="h-5 w-5" /> : rejected ? <XCircle className="h-5 w-5" /> : done ? <Clock className="h-5 w-5" /> : <FileText className="h-5 w-5" />}
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{item.label}{item.required && <span className="text-red-500"> *</span>}</p>
              {badge && <span data-testid={`doc-status-${item.key}`} className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${badge.c}`}>{badge.t}</span>}
            </div>
            {done ? <a href={fileDownloadUrl(docs[0].id)} target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-rose-600 hover:underline">{docs[0].original_filename}</a> : <p className="text-xs text-slate-400 dark:text-slate-500">Not uploaded</p>}
            {verified && docs[0].status_at && <p className="text-[11px] text-emerald-600">Verified by broker · {new Date(docs[0].status_at).toLocaleDateString()}</p>}
            {rejected && docs[0].status_note && <p className="text-[11px] text-red-600">{docs[0].status_note}</p>}
          </div>
        </div>
        <label className="cursor-pointer rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-semibold text-slate-700 dark:text-slate-300 hover:bg-slate-50" data-testid={`upload-${item.key}`}>
          {uploading === item.key ? "…" : done ? "Replace" : "Upload"}
          <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => upload(item.key, e.target.files[0])} />
        </label>
      </div>
    );
  };

  const toggleAck = async (key, accepted) => {
    try { await api.patch(`/requests/${r.id}/acknowledge`, { key, accepted }); onDone(); }
    catch { toast.error("Could not update disclosure"); }
  };

  return (
    <div className="space-y-4">
      <Card title="You provide" icon={FolderCheck}>
        <p className="mb-4 text-sm text-slate-600 dark:text-slate-300">Upload these from your side to close your deal. Accepted: PDF, JPG, PNG. Everything is stored securely.</p>
        <div className="space-y-3">{buyerUploads.map((item) => <UploadRow key={item.key} item={item} />)}</div>
      </Card>

      <Card title="Broker prepares → you sign" icon={FileText}>
        <p className="mb-4 text-sm text-slate-600 dark:text-slate-300">Your broker prepares these documents. Review, sign, and upload the signed copy here.</p>
        <div className="space-y-3">{brokerUploads.map((item) => <UploadRow key={item.key} item={item} />)}</div>
      </Card>

      <Card title="California disclosures" icon={ShieldCheck}>
        <p className="mb-4 text-sm text-slate-600 dark:text-slate-300">Review and acknowledge each required California disclosure. These are mandatory closing acknowledgments for vehicle purchases in California.</p>
        <div className="space-y-3">
          {disclosures.map((item) => {
            const done = !!acks[item.key];
            return (
              <label key={item.key} data-testid={`ack-${item.key}`} className={`flex cursor-pointer gap-3 rounded-lg border p-4 transition-colors ${done ? "border-emerald-200 bg-emerald-50/50" : "border-slate-200 dark:border-slate-800 hover:border-slate-300"}`}>
                <Checkbox checked={done} onCheckedChange={(v) => toggleAck(item.key, !!v)} data-testid={`ack-check-${item.key}`} className="mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{item.label}{item.required && <span className="text-red-500"> *</span>}</p>
                  <p className="mt-0.5 text-xs leading-relaxed text-slate-500 dark:text-slate-400">{item.text}</p>
                  {done && <p className="mt-1 text-xs font-medium text-emerald-600">Acknowledged {new Date(acks[item.key].at).toLocaleDateString()}</p>}
                </div>
              </label>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

function DeliveryForm({ r, onDone }) {
  const d = r.delivery;
  const deliveryUnavailable = !!d?.delivery_unavailable;
  const deliveryAvailable = !!r.delivery_availability?.available && !deliveryUnavailable;
  const [form, setForm] = useState(d || { method: "pickup", address: "", zip_code: "", preferred_date: "", preferred_window: "", notes: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    try { await api.patch(`/requests/${r.id}/delivery`, form); toast.success("Delivery preference saved"); onDone(); }
    catch { toast.error("Could not save"); }
  };
  const [rescheduleDate, setRescheduleDate] = useState("");
  const [showReschedule, setShowReschedule] = useState(false);
  const requestReschedule = async () => {
    if (!rescheduleDate) { toast.error("Pick a new date"); return; }
    try { await api.patch(`/requests/${r.id}/delivery/reschedule`, { requested_date: rescheduleDate }); toast.success("New date requested · your broker will confirm"); setShowReschedule(false); onDone(); }
    catch { toast.error("Could not send request"); }
  };
  return (
    <Card title="Pickup or delivery" icon={Truck}>
      {d?.confirmed && (
        <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700" data-testid="delivery-confirmed">
          <p className="flex items-center gap-2 font-semibold"><CheckCircle2 className="h-4 w-4 shrink-0" /> Confirmed for {d.confirmed_date}{d.start_at ? ` at ${fmtTime(d.start_at)}${d.end_at ? ` – ${fmtTime(d.end_at)}` : ""} PT` : ""} · {d.method === "delivery" ? `delivery to ${d.address || "your address"}` : "dealer pickup"}</p>
          {(d.time_window || d.contact) && <p className="mt-1 pl-6 text-xs text-emerald-600">{d.time_window && `Time window: ${d.time_window}`}{d.time_window && d.contact && " · "}{d.contact && `Contact: ${d.contact}`}</p>}
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/requests/${r.id}/delivery.ics`} data-testid="add-to-calendar" className="mt-2 inline-flex items-center gap-1 pl-6 text-xs font-bold text-emerald-800 underline">Add to calendar</a>
        </div>
      )}
      {deliveryUnavailable && (
        <div className="mb-4 border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="buyer-delivery-unavailable">
          <p className="font-bold">Delivery is unavailable for this deal</p>
          <p className="mt-1 text-xs leading-5">{d.delivery_unavailable_reason} Dealer pickup is available instead—please select a pickup date below.</p>
        </div>
      )}
      {!deliveryUnavailable && !deliveryAvailable && (
        <div className="mb-4 border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300" data-testid="buyer-delivery-awaiting-verification">
          <p className="font-bold">Home / work delivery requires broker verification</p>
          <p className="mt-1 text-xs leading-5">Dealer pickup is available now. Your broker will notify you if home or work delivery becomes available for this deal.</p>
        </div>
      )}
      {deliveryAvailable && (
        <div className="mb-4 border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="buyer-delivery-available">
          <p className="font-bold">Home / work delivery is available</p>
          <p className="mt-1 text-xs leading-5">Your broker verified delivery for this deal. You can choose delivery or dealer pickup below.</p>
        </div>
      )}
      {r.handoff?.completed_at && (
        <div className="mb-4 border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="buyer-handoff-complete">
          <p className="font-bold">{r.handoff.method === "delivery" ? "Delivery" : "Pickup"} completed</p>
          <p className="mt-1 text-xs">Your deal was completed on {new Date(r.handoff.completed_at).toLocaleString()}.</p>
        </div>
      )}
      {d?.confirmed && !r.handoff?.completed_at && (
        <div className="mb-4" data-testid="reschedule-section">
          {d.reschedule_requested ? (
            <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm font-semibold text-amber-700" data-testid="reschedule-pending"><Clock className="h-4 w-4 shrink-0" /> New date requested ({d.reschedule_date}) — awaiting broker confirmation.</div>
          ) : showReschedule ? (
            <div className="flex flex-wrap items-end gap-2 rounded-lg border border-slate-200 dark:border-slate-800 p-3">
              <div className="flex-1"><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">New preferred date</Label><Input type="date" data-testid="reschedule-date" value={rescheduleDate} onChange={(e) => setRescheduleDate(e.target.value)} className="mt-1.5" /></div>
              <Button data-testid="reschedule-submit" onClick={requestReschedule} className="rounded-md bg-slate-950 font-semibold hover:bg-slate-800">Send request</Button>
              <Button variant="ghost" onClick={() => setShowReschedule(false)} className="text-slate-500 dark:text-slate-400">Cancel</Button>
            </div>
          ) : (
            <button onClick={() => setShowReschedule(true)} data-testid="reschedule-open" className="text-sm font-semibold text-rose-600 hover:underline">Need a different date? Request a reschedule</button>
          )}
        </div>
      )}
      {d && !d.confirmed && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm font-semibold text-amber-700" data-testid="delivery-pending">
          <Clock className="h-4 w-4 shrink-0" /> Awaiting broker confirmation of your preferred date.
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Method</Label>
          <Select value={form.method} onValueChange={(v) => set("method", v)} disabled={deliveryUnavailable}><SelectTrigger className="mt-1.5" data-testid="del-method"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="pickup">Dealer pickup</SelectItem><SelectItem value="delivery" disabled={!deliveryAvailable}>Home / work delivery{!deliveryAvailable ? " · Broker verification required" : ""}</SelectItem></SelectContent></Select>
        </div>
        <div><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Preferred date</Label><Input data-testid="del-date" type="date" value={form.preferred_date} onChange={(e) => set("preferred_date", e.target.value)} className="mt-1.5" /></div>
        <div><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Time window</Label><Select value={form.preferred_window || ""} onValueChange={(value) => set("preferred_window", value)}><SelectTrigger className="mt-1.5" data-testid="del-window"><SelectValue placeholder="Choose a window" /></SelectTrigger><SelectContent><SelectItem value="morning">Morning (8–12)</SelectItem><SelectItem value="afternoon">Afternoon (12–4)</SelectItem><SelectItem value="evening">Evening (4–7)</SelectItem></SelectContent></Select></div>
      </div>
      {r.dealer_contact && <div className="mt-4 border border-slate-200 bg-slate-50 p-3 text-sm dark:border-slate-800 dark:bg-slate-950" data-testid="buyer-delivery-dealer-info"><p className="font-bold">Dealer handoff details</p><p className="mt-1">{r.dealer_contact.dealership_name}</p><p className="text-slate-600 dark:text-slate-300">{r.dealer_contact.dealership_address}</p><p className="mt-1 text-slate-600 dark:text-slate-300">{r.dealer_contact.contact_person || r.dealer_contact.name} · {r.dealer_contact.phone}</p></div>}
      <Textarea data-testid="del-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="mt-4" placeholder="Scheduling notes…" />
      <Button data-testid="del-submit" onClick={submit} className="mt-4 rounded-md bg-slate-950 font-semibold hover:bg-slate-800">Save preference</Button>
    </Card>
  );
}
